import EventEmitter from 'eventemitter3';
import { CustomizeItemCore } from '../core/customize-item-core';
import { CustomizeItemUIBase, ICustomizeItemUiParams } from '../ui/base/customize-item-ui-base';
import { Data } from '../../../../models/data';
import { Context } from '../../../../models/context';
import { LocalizationService } from '../../../../services/localization-service';
import { Order } from '../../../../models/order';
import { DataLayerService } from '../../../../services/data-layer-service';
import { ProfileService } from '../../../../services/profile-service';
import { CustomizeItemUISingleItem } from '../ui/single-item/customize-item-ui-single-item';
import { CustomizeItemUIMultiItem } from '../ui/multi-item/customize-item-ui-multi-item';
import { EventTypes } from '../../../../core/event-bus';
import { CustomizeItemUiOption, Modals } from '../../../../types/enums';
import { ICustomizeItemProps } from '../customize-item.interface';
import { CustomizeItemFavorite } from '../ui/shared/customize-item-favorite';

export interface ICustomizeItemControllerParams {
    eventBus: EventEmitter,
    data: Data,
    order: Order,
    context: Context,
    localizationService: LocalizationService,
    dataLayerService: DataLayerService,
    profileService: ProfileService
}

/**
 * Acts as the main controller for the item customization flow.
 * Manages shared dependencies, event subscriptions, core logic, and UI rendering mode (single vs multi).
 */
export class CustomizeItemController {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _profileService: ProfileService;

    private readonly _core: CustomizeItemCore;
    private readonly _favorite: CustomizeItemFavorite;
    private readonly _uiSingle: CustomizeItemUISingleItem;
    private readonly _uiMulti: CustomizeItemUIMultiItem;
    private _activeUi: CustomizeItemUIBase;

    /**
     * @param {EventEmitter} eventBus - Shared event bus for app-wide communication.
     * @param {Data} data - Global application data.
     * @param {Order} order - The current user's order instance.
     * @param {Context} context - Runtime context (language, location, etc).
     * @param {LocalizationService} localizationService - For localized UI strings.
     * @param {DataLayerService} dataLayerService - Service for analytics/event tracking.
     * @param {ProfileService} profileService - User profile management service.
     */
    constructor(params: ICustomizeItemControllerParams) {
        this._eventBus = params.eventBus;
        this._data = params.data;
        this._order = params.order;
        this._context = params.context;
        this._localizationService = params.localizationService;
        this._dataLayerService = params.dataLayerService;
        this._profileService = params.profileService;

        this._core = new CustomizeItemCore(
            this._eventBus,
            this._data,
            this._order,
            this._context,
            this._profileService,
            this._localizationService,
            this._dataLayerService
        );

        this._favorite = new CustomizeItemFavorite({
            eventBus: this._eventBus,
            order: this._order,
            context: this._context,
            profileService: this._profileService,
            localizationService: this._localizationService,
            dataLayerService: this._dataLayerService,
            core: this._core
        });

        const uiParams: ICustomizeItemUiParams = {
            eventBus: this._eventBus,
            data: this._data,
            context: this._context,
            localizationService: this._localizationService,
            order: this._order,
            dataLayerService: this._dataLayerService,
            profileService: this._profileService,
            core: this._core,
            favorite: this._favorite
        };

        this._uiSingle = new CustomizeItemUISingleItem(uiParams);
        this._uiMulti = new CustomizeItemUIMultiItem(uiParams);

        this._activeUi = this._uiSingle;
        this._eventBusListeners();
    }

    /**
     * Gets the current UI instance (single or multi).
     * @returns {CustomizeItemUIBase} The UI controller instance.
     */
    get ui(): CustomizeItemUIBase {
        return this._activeUi;
    }

    /**
     * Registers listeners to respond to application-wide events.
     * Currently listens for store changes to potentially update the UI mode.
     */
    protected _eventBusListeners(): void {
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._changeUiView);
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, props: ICustomizeItemProps) => {
            if (modal != Modals.CUSTOMIZE_ITEM)
                return;

            this._core.setParams(props.mItem.ItemId, props.sizeId, props.quantity, props.itemsToEdit, props.mealDeal, props.recentOrder, props.elementIndex);
        });
    }

    /**
     * Changes the UI view mode (single or multi) when store-related data changes.
     * Currently stubbed — uncomment logic when dynamic switching is supported.
     */
    private readonly _changeUiView = (): void => {
        const isSingle = this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.SINGLE_ITEM;
        this._activeUi = isSingle ? this._uiSingle : this._uiMulti;
    }
}